import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck } from 'lucide-react';
import { usePendingActionsCount } from './usePendingActions';

/**
 * TopBar entry: a pending-actions count next to the notifications bell.
 * Renders nothing while the user has nothing pending — no dead chrome.
 * Same bubble treatment as components/badge, plus a 99+ cap and a name
 * for screen readers.
 */
const TopBarPendingActions: React.FC = () => {
  const { t } = useTranslation('requests');
  const navigate = useNavigate();
  const { count } = usePendingActionsCount();

  if (count <= 0) return null;
  const display = count > 99 ? '99+' : String(count);

  return (
    <button
      type="button"
      onClick={() => navigate('/pending-actions')}
      aria-label={t('{{n}} pending actions', { n: count })}
      title={t('Pending actions')}
      data-testid="pa-topbar-badge"
      className="relative rounded-lg p-2 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <ClipboardCheck size={20} className="text-muted-foreground" aria-hidden="true" />
      <span
        aria-hidden="true"
        className="absolute top-0.5 end-0.5 z-10 rounded-[10px] bg-red-600 px-[5px] text-xs font-medium leading-[18px] text-white"
      >
        {display}
      </span>
    </button>
  );
};

export default TopBarPendingActions;
